import type { Schema } from '@/shared/api/contract';
import { liveDecisionOf } from '@/shared/emails/live';

export { MANUAL_MAILBOX, mailboxLabel } from '@/shared/emails/types';

/** Straight from the contract: cubas.domain.schemas.email. */
export type EmailStatus = Schema<'EmailStatus'>;
export type EmailAttachment = Schema<'EmailAttachmentRead'>;
export type TriageDecision = Schema<'TriageDecisionRead'>;
export type TriageCorrection = Schema<'TriageCorrection'>;
/** What a proposal rests on (contract v10.30): a rule, the model, the memory, a bulk footer. */
export type TriageBasis = Schema<'TriageBasis'>;
/** Why the system is not sure about a proposal (contract v10.30). */
export type DoubtReason = Schema<'DoubtReason'>;
export type Email = Schema<'EmailRead'>;
export type EmailDetail = Schema<'EmailDetail'>;
export type TriagePlan = Schema<'TriagePlan'>;
export type AttachmentUrl = Schema<'AttachmentUrl'>;
export type IngestStats = Schema<'IngestStats'>;
export type ReadAllResult = Schema<'ReadAllResult'>;
export type InboxView = Schema<'InboxView'>;
export type InboxViews = Schema<'InboxViews'>;
export type ViewCount = Schema<'ViewCount'>;
export type FolderCount = Schema<'FolderCount'>;
export type EmailNeighbours = Schema<'EmailNeighbours'>;

/**
 * Unread for the person looking (contract v10.22). Only an explicit `false`: a back
 * older than v10.22 sends no `is_read`, and then nothing is bold and nothing is marked.
 */
export function isUnread(email: { is_read?: boolean | undefined }): boolean {
  return email.is_read === false;
}

/**
 * How a status reads on a badge. Only by exception: a processed email carries none.
 * `pending` says what is happening («Clasificando…»), not the state of a row: since
 * contract v10.25 there is no status filter, and «Por revisar» holds these too.
 */
export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = {
  pending: 'Clasificando…',
  processed: 'Procesado',
  error: 'Error',
};

/**
 * The decisions of an email, oldest first. Never the array as it arrived.
 *
 * The back does order the relationship, and there is a test in `Cubas_Back` pinning it.
 * Sorting again here is not distrust for its own sake: the last element of this list is
 * what the screen calls "the live decision", and what it does with it is write a
 * confirmation into the accuracy figure of the project. If that order is ever wrong, the
 * damage is a hit recorded against a proposal nobody was looking at, silently, in the one
 * dataset F3 exists to build. Ten lines of sort are cheap next to that.
 */
export function sortedDecisions(email: {
  triage_decisions?: TriageDecision[] | null;
}): TriageDecision[] {
  return [...(email.triage_decisions ?? [])].sort((a, b) =>
    a.created_at === b.created_at
      ? a.id.localeCompare(b.id)
      : a.created_at.localeCompare(b.created_at),
  );
}

/**
 * The live decision of an email, or undefined when nobody has decided yet.
 *
 * The newest one a person answered and, while nobody has answered any, the newest
 * (contract v10.24, ADR 0020 of the back). It is the row `POST /triage/correct` writes on
 * and the one the screen shows. The earlier ones are previous prompt versions and are
 * kept on purpose: a reclassification adds a row, it never overwrites what the system
 * said the first time, and it never hides what a person answered.
 */
export function liveDecision(email: {
  triage_decisions?: TriageDecision[] | null;
}): TriageDecision | undefined {
  return liveDecisionOf(email.triage_decisions);
}

/** What the decision says the email is: the person's category, or the proposal. */
export function decisionCategory(decision: TriageDecision): TriageDecision['category'] {
  return decision.corrected_category ?? decision.category;
}
